/**
 * FA リクエストへ自動付与する Space コンテキスト。
 *
 * 明示的に設定された `spaceCode` だけをモジュールメモリに保持する。cookie / localStorage /
 * sessionStorage は使わず、React にも依存しないため RestClient から直接参照できる
 * （循環依存にならない）。
 *
 * 既定値は生成しない。未設定ならリクエストに Space の key 自体を含めず、`spaceCode` は `""`、
 * `spaceAsOf` は現在時刻というサーバ側の既定値に委ねる。
 */

/** 明示的に設定された Space code。未設定・クリア後は `undefined`。 */
let spaceCode: string | undefined;

/** 現在の Space code。未設定なら `undefined`。 */
function getSpaceCode(): string | undefined {
  return spaceCode;
}

/**
 * Space 選択画面などから現在の Space code を設定する。
 *
 * `null`／`undefined`／空文字列／空白のみはいずれも未設定として扱う。
 */
function setSpaceCode(code: string | null | undefined): void {
  spaceCode = code?.trim() || undefined;
}

/** ログアウト・会社切替などで呼び、前セッションの値を残さない。 */
function clearSpaceCode(): void {
  spaceCode = undefined;
}

/** リクエストへ付与する Space パラメータ。未設定なら key を持たない空オブジェクトを返す。 */
function getSpaceRequestParameters(): { spaceCode?: string } {
  return spaceCode === undefined ? {} : { spaceCode };
}

/**
 * GET/POST/PUT/DELETE 共通で Space コンテキストを合成する。
 *
 * 既存の query parameter・body は保持する。`spaceCode` が設定されている場合のみ spread の前に
 * 追加するため、呼び出し側が明示した同名キーが state の値より優先される（比較画面など、
 * タブの現在の台帳以外を指定する API のため）。未設定なら key 自体を追加しない（既定値は
 * サーバ側が決める）。呼び出し側の値は変更しない。
 *
 * 配列（POST/PUT の body）は spread すると index を key にした object に変わり wire shape が
 * 壊れるため、合成せずそのまま返す。
 */
function withSpace<T extends object>(value: T): T {
  return Array.isArray(value) ? value : ({ ...getSpaceRequestParameters(), ...value } as T);
}

export { clearSpaceCode, getSpaceCode, getSpaceRequestParameters, setSpaceCode, withSpace };
